import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Col, Form, Icon, Row, Upload, Table, Tag, DatePicker, Select, Menu, Dropdown, Pagination } from "antd";
import CustomRxPageHeader from "../../rxcomponents/CustomRxPageHeader";
import {
    downloadBankTransferTemplateFile,
    downloadBatchFile,
    getNewBatchUploads,
    uploadBatchTransferFile
} from "../../api/api";
import { displayError, displayGenericError, displaySuccessMessageWithSeconds } from "../../common/notifications";
import withUseIntl from "../../components/withUseIntl";
import moment from "moment";
import { DEFAULT_PAGE_SIZE_10 } from "../../common/Const";
import '../../index.scss';
import './BatchUploadPage.css';

const pageName = "BatchUploadPage";
const { Option } = Select;

export function BatchUploadPage(props) {
    const intl = useIntl();
    const pageName = "BatchUploadPage";

    const [status, setStatus] = useState("");
    const [from, setFrom] = useState(moment().subtract(3, "months"));
    const [to, setTo] = useState(moment());

    const [batchUploads, setBatchUploads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [totalItem, setTotalItem] = useState(0);

    useEffect(() => {
        getData(page);
    }, [page, status, from, to]);

    const validateDates = (fromDate, toDate) => {
        if (fromDate && toDate && toDate.isBefore(fromDate)) {
            displayError("The 'To' date cannot be earlier than the 'From' date.");
            return false;
        }
        return true;
    };

    const handleFromChange = (newValue) => {
        if (!validateDates(newValue, to)) {
            setFrom(null);
        } else {
            setFrom(newValue);
            if (window.gtag) {
                window.gtag('event', 'BatchUpload_From_date_filter_change', {
                    'event_category': 'Filters',
                    'event_label': `From Date - ${pageName}`,
                    'value': newValue ? newValue.format("YYYY-MM-DD") : 'null'
                });
            }
        }
    };

    const handleToChange = (newValue) => {
        if (!validateDates(from, newValue)) {
            setTo(null);
        } else {
            setTo(newValue);
            if (window.gtag) {
                window.gtag('event', 'BatchUpload_To_date_filter_change', {
                    'event_category': 'Filters',
                    'event_label': `To Date - ${pageName}`,
                    'value': newValue ? newValue.format("YYYY-MM-DD") : 'null'
                });
            }
        }
    };

    const handleStatusChange = (newValue) => {
        setStatus(newValue);
        if (window.gtag) {
            window.gtag('event', 'BatchUpload_status_filter_change', {
                'event_category': 'Filters',
                'event_label': `Status - ${pageName}`,
                'value': newValue
            });
        }
    };

    const getData = (page) => {
        setLoading(true);

        const filters = {
            status: status,
            dateFrom: from ? from.format("YYYY-MM-DD") : null,
            dateTo: to ? to.format("YYYY-MM-DD") : null
        };

        getNewBatchUploads(page, DEFAULT_PAGE_SIZE_10, filters).then(response => {
            // console.log('API Response:', response);
            setBatchUploads(response.content);
            setLoading(false);
            setTotalItem(response.totalElements);
        })
            .catch(error => {
                displayGenericError(error);
                setLoading(false);
            });
    };

    const downloadTemplateFile = () => {
        downloadBankTransferTemplateFile();
    }

    const downloadTemplateFileXLSX = () => {
        downloadBankTransferTemplateFile("xlsx");
    }

    const processUpload = ({ file }) => {
        uploadBatchTransferFile(file)
            .then(response => {
                getData(page);
                let msg = intl.formatMessage({ id: "app.common.successfully" });
                displaySuccessMessageWithSeconds(msg, 2);
            }).catch(e => {
                displayError(e.result);
            });
    }

    const onDownloadLinkClicked = id => {
        downloadBatchFile(id).then(response => {
        }).catch(error => {
            displayGenericError(error);
        });
    }

    const columns = [
        {
            title: intl.formatMessage({ id: "app.page.batch.upload.upload.date" }),
            dataIndex: "dateCreated",
            key: "dateCreated",
            render: dateCreated => `${moment(dateCreated).format("YYYY-MM-DD HH:mm:ss")}`
        },
        { title: intl.formatMessage({ id: "app.page.batch.upload.upload.by" }), dataIndex: "uploadBy", key: "uploadBy" },
        {
            title: intl.formatMessage({ id: "app.common.status" }), dataIndex: "status", key: "status",
            render: (text, record) => {
                if (text === 'CANCELLED') {
                    return <Tag color={'red'}>{text.toUpperCase()}</Tag>;
                } else {
                    return <Tag>{text.toUpperCase()}</Tag>;
                }
            }
        },
        { title: intl.formatMessage({ id: "app.common.reason" }), dataIndex: "note", key: "note" },
        {
            title: intl.formatMessage({ id: "app.page.batch.upload.batch.file" }),
            dataIndex: "uploadFilePath",
            key: "uploadFilePath",
            render: (text, record) => (
                <a onClick={() => onDownloadLinkClicked(text)}>{intl.formatMessage({ id: "app.common.download" })}</a>
            )
        },
    ];

    let currentPage = page + 1;

    const menu = (
        <Menu>
            <Menu.Item key="1" onClick={downloadTemplateFile}>
                {intl.formatMessage({ id: "app.page.batch.upload.batch-upload-file-template-csv" })}
            </Menu.Item>
            <Menu.Item key="2" onClick={downloadTemplateFileXLSX}>
                {intl.formatMessage({ id: "app.page.batch.upload.batch-upload-file-template-xlsx" })}
            </Menu.Item>
        </Menu>
    );

    const disabledFromDate = (current) => {
        return current && (current > moment().endOf('day') || current < moment().subtract(5, 'years'));
    };

    const disabledToDate = (current) => {
        return current && (current > moment().endOf('day') || current < from);
    };

    return (
        <div>
            <CustomRxPageHeader customer={props.customer}
                title={intl.formatMessage({ id: "app.sidebar.batch.upload" })}
            />
            <div className="bp-page-content bp-page-large">
                <div className={"content-section"}>
                    <div>
                        <Form name="uploadForm">
                            <Row gutter={[16, 16]} className="button-row">
                                <Col xs={12} sm={8} md={8} lg={6}>
                                    <Upload
                                        defaultFileList={[]}
                                        customRequest={processUpload}
                                        showUploadList={false}
                                    >
                                        <Button className="upload-button" block>
                                            <div className="button-content">
                                                <Icon type="upload" className="upload-icon" />
                                                <span className="upload-text">
                                                    {intl.formatMessage({ id: "app.page.batch.upload.batch-upload-transfer-order" })}
                                                </span>
                                            </div>
                                        </Button>
                                    </Upload>
                                </Col>
                                <Col xs={12} sm={8} md={8} lg={6}>
                                    <Dropdown overlay={menu}>
                                        <Button className="dropdown-button" block>
                                            <div className="button-content">
                                                <Icon type="download" className="upload-icon" />
                                                <span className="dropdown-text">
                                                    {intl.formatMessage({ id: "app.page.batch.upload.download-file-template" })}
                                                </span>
                                                <Icon type="down" className="dropdown-icon" />
                                            </div>
                                        </Button>
                                    </Dropdown>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={12} sm={12} lg={6}>
                                    <Form.Item label={intl.formatMessage({ id: "app.common.from" })} required={true}>
                                        <DatePicker
                                            size={"large"}
                                            format={"YYYY-MM-DD"}
                                            value={from}
                                            onChange={handleFromChange}
                                            disabledDate={disabledFromDate}
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={12} lg={6}>
                                    <Form.Item label={intl.formatMessage({ id: "app.common.to" })} required={true}>
                                        <DatePicker
                                            size={"large"}
                                            format={"YYYY-MM-DD"}
                                            value={to}
                                            onChange={handleToChange}
                                            disabledDate={disabledToDate}
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item label={intl.formatMessage({ id: "app.common.status" })}>
                                        <Select
                                            defaultValue=""
                                            onChange={handleStatusChange}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="">All</Option>
                                            <Option value="NEW">New</Option>
                                            <Option value="COMPLETED">Completed</Option>
                                            <Option value="CANCELLED">Cancelled</Option>
                                            <Option value="ERROR">Error</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <br />

                    <div>
                        <Table
                            loading={loading}
                            pagination={false}
                            columns={columns}
                            dataSource={batchUploads}
                        />
                    </div> <br />

                    <div style={{ float: "right" }}>
                        <Pagination current={currentPage} pageSize={DEFAULT_PAGE_SIZE_10} total={totalItem} onChange={(value) => {
                            getData(value - 1);
                            setPage(value - 1);
                        }} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default withUseIntl(BatchUploadPage);
